<template>
  <div class="kt-portlet kt-portlet--height-fluid">
    <div
      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm"
    >
      <div class="kt-portlet__head-label">
        <span class="kt-portlet__head-icon">
          <i class="fal fa-chalkboard-teacher"></i>
        </span>
        <h3 class="kt-portlet__head-title">LMS Curriculum Manager</h3>
      </div>
    </div>
    <div class="kt-portlet__body">
      <confirm-dialog
        :confirm-action="deleteItem"
        :show-dialog="showConfirmDialog"
        :close-dialog="closeConfirmDialog"
        :loading="isDeleting"
      >
        Are you sure you want to proceed?
      </confirm-dialog>
      <v-data-table
        :search="search"
        :headers="headers"
        :disabled="isFetching"
        :items="filteredCourses"
        :items-per-page="pagination.per_page"
        :server-items-length="pagination.total"
        @update:page="changePage"
        @update:sort-by="updateSorting"
        @update:sort-desc="updateSorting"
        :options.sync="options"
        class="elevation-1"
        no-results-text="No results..."
        :loading="isFetching"
      >
        <template v-slot:item.name="{ item }">
          <v-list-item-content>
            <v-list-item-title>
              <v-tooltip v-if="item.name.length > 25" top>
                <template #activator="{ on }">
                  <h4 v-on="on">{{ trimString(item.name) }}</h4>
                </template>
                <span>{{ item.name }}</span>
              </v-tooltip>
              <h4 v-else>{{ item.name }}</h4>
            </v-list-item-title>
            <v-list-item-subtitle
              v-html="'Created By: ' + item.author.name"
            ></v-list-item-subtitle>
            <v-list-item-subtitle
              color="error"
              v-html="'Last Edited By: ' + item.last_edit_by.name"
            ></v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <template v-slot:item.dealers="{ item }">
          <v-tooltip v-if="item.dealers.length > 3" top max-width="500">
            <template #activator="{ on }">
              <span v-on="on">
                {{ trimArray(item.dealers) }}
              </span>
            </template>
            <span class="small">
              {{
                item.dealers
                  .map(function (specific_dealer) {
                    return specific_dealer.name;
                  })
                  .toString()
              }}
            </span>
          </v-tooltip>
          <span v-else>
            {{
              item.dealers
                .map(function (specific_dealer) {
                  return specific_dealer.name;
                })
                .toString()
            }}
          </span>
        </template>
        <template v-slot:item.specific_dealers="{ item }">
          <v-tooltip
            v-if="item.specific_dealers.length > 3"
            top
            max-width="500"
          >
            <template #activator="{ on }">
              <span v-on="on">
                {{ trimArray(item.specific_dealers) }}
              </span>
            </template>
            <span class="small">
              {{
                item.specific_dealers
                  .map(function (specific_dealer) {
                    return specific_dealer.name;
                  })
                  .toString()
              }}
            </span>
          </v-tooltip>
          <span v-else>
            {{
              item.specific_dealers
                .map(function (specific_dealer) {
                  return specific_dealer.name;
                })
                .toString()
            }}
          </span>
        </template>
        <template v-slot:top>
          <div flat color="white" class="w-100 pa-3">
            <div class="row no-gutters mt-3">
              <div class="col-xl-4">
                <v-select
                  label="Filter Status"
                  :loading="isFetching"
                  :disabled="isFetching"
                  @change="fetchCurriculums"
                  dense
                  v-model="filters"
                  multiple
                  prepend-inner-icon="fas fa-filter"
                  :items="['Active', 'Inactive', 'Published', 'Drafts']"
                >
                  <template v-slot:prepend-item>
                    <v-list-item
                      v-if="filters.length <= 0"
                      ripple
                      @click="toggleAllFilter"
                    >
                      <v-list-item-content>
                        <v-list-item-title>All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-else ripple @click="removeAllFilter">
                      <v-list-item-content>
                        <v-list-item-title>Deselect All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-select>
              </div>
              <div class="col-xl-1"></div>
              <div class="col-xl-7">
                <div class="d-flex flex-wrap flex-sm-nowrap">
                  <div class="w-100">
                    <v-text-field
                      :clearable="true"
                      v-model.trim="search"
                      @change="searchCurriculums"
                      append-icon="fal fa-search"
                      label="Search (By Name, Company, Sub-Company, Author, Last Edited By)"
                      single-line
                      hide-details
                      class="mt-0 pt-0 mr-2"
                    />
                  </div>
                  <div
                    class="d-flex flex-wrap flex-sm-nowrap justify-content-center"
                  >
                    <v-btn
                      class="mt-3 mt-sm-0"
                      color="primary"
                      :disabled="isFetching"
                      :loading="isFetching"
                      @click="toggleAddCurriculumDialog"
                      text
                    >
                      <v-icon class="mr-2" small>fal fa-plus</v-icon>New
                      Curriculum
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-slot:item.is_draft="{ item }">
          <span :class="item.is_draft ? '' : 'text-success'">
            {{ item.is_draft ? 'Draft' : 'Published' }}
          </span>
        </template>
        <template v-slot:item.created_at="{ item }">{{
          item.created_at | displayDateTime
        }}</template>
        <template v-slot:item.action="{ item }">
          <template v-if="item.deleted_at === null">
            <v-tooltip top>
              <template #activator="{ on }">
                <v-btn
                  v-if="isSuperAdmin"
                  v-on="on"
                  text
                  :to="'/lms-admin/curriculum/' + item.id"
                  color="grey darken-3"
                  small
                >
                  <v-icon small>fal fa-cog</v-icon>
                </v-btn>
              </template>
              <span>Configure {{ item.name }}</span>
            </v-tooltip>
            <v-btn
              text
              @click="toggleEditCurriculumDialog(item)"
              color="grey darken-3"
              small
            >
              <v-icon small>fal fa-edit</v-icon>
            </v-btn>
            <!--            <v-btn-->
            <!--              text-->
            <!--              @click="duplicateItem(item)"-->
            <!--              color="grey darken-3"-->
            <!--              small-->
            <!--            >-->
            <!--              <v-icon small>fal fa-copy</v-icon>-->
            <!--            </v-btn>-->
            <v-btn
              text
              @click="previewConfirmDialog(item)"
              color="red darken-3"
              small
            >
              <v-icon small>fal fa-trash</v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-btn text @click="restoreItem(item)" color="blue darken-3" small>
              <v-icon small class="mr-2">fal fa-trash-restore</v-icon>
              Restore
            </v-btn>
          </template>
        </template>
        <template v-slot:no-data>
          <div class="mt-1 mb-1">
            No data available <br />
            <v-btn color="primary" @click="fetchCurriculums">Reset</v-btn>
          </div>
        </template>
      </v-data-table>
    </div>
    <duplicate-items
      v-model="showDuplicateItem"
      :item="dupItem"
      type="course"
    ></duplicate-items>
    <add-curriculum-dialog
      :add-curriculum-dialog="addCurriculumDialog"
      :toggle-add-curriculum-dialog="toggleAddCurriculumDialog"
      :closeModal="toggleClose"
      v-on:course="fetchCurriculums"
    ></add-curriculum-dialog>
    <edit-curriculum-dialog
      :curriculum="editedItem"
      :edit-curriculum-dialog="editCurriculumDialog"
      :toggle-edit-curriculum-dialog="toggleEditCurriculumDialog"
      :close-modal="toggleClose"
      v-on:course="fetchCurriculums"
      :key="key"
    >
    </edit-curriculum-dialog>
  </div>
</template>
<script>
import {
  map as _map,
  head as _head,
  sortBy as _sortBy,
  some as _some,
} from 'lodash-es';
import EventBus from '../../plugins/eventBus';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog';
import DuplicateItems from '@/components/lms-admin/includes/DuplicateItems';
import AddCurriculumDialog from '@/components/course-certification/includes/AddCurriculumDialog';
import EditCurriculumDialog from '@/components/course-certification/includes/EditCurriculumDialog';

export default {
  name: 'CourseSection',
  components: {
    ConfirmDialog,
    DuplicateItems,
    AddCurriculumDialog,
    EditCurriculumDialog,
  },
  data() {
    return {
      selectedIdForDeletion: '',
      filters: [],
      search: '',
      dealers: [],
      sortBy: [],
      isSaving: false,
      isFetching: false,
      isDeleting: false,
      pagination: {
        total: 1,
        per_page: 5,
        current_page: 1,
      },
      dialog: false,
      headers: [
        {
          text: 'Curriculum',
          align: 'left',
          value: 'name',
          sortable: true,
        },
        {
          text: 'Companies',
          align: 'center',
          value: 'dealers',
          sortable: true,
        },
        {
          text: 'Sub-Companies',
          align: 'center',
          value: 'specific_dealers',
          sortable: true,
        },
        {
          text: 'Status',
          align: 'center',
          value: 'is_draft',
          sortable: true,
        },
        {
          text: 'Date Created',
          align: 'center',
          value: 'created_at',
          sortable: true,
        },
        {
          text: 'Action',
          align: 'center',
          value: 'action',
          width: 200,
          sortable: false,
        },
      ],
      editedIndex: -1,
      editedItem: {
        name: '',
        assignedDealer: '',
      },
      defaultItem: {
        name: '',
        assignedDealer: '',
      },
      options: {
        groupBy: [],
        groupDesc: [],
        itemsPerPage: 5,
        multiSort: false,
        mustSort: false,
        page: 1,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      showConfirmDialog: false,

      showDuplicateItem: false,
      dupItem: null,
      addCurriculumDialog: false,
      editCurriculumDialog: false,
      key: 0,
    };
  },

  created() {
    this.toggleAllFilter();
  },

  computed: {
    user() {
      return this.$auth.user();
    },
    isSuperAdmin() {
      return _some(this.user.roles, { name: 'super-administrator' });
    },
    formTitle() {
      return this.editedIndex === -1 ? 'New Course' : 'Edit Course';
    },
    filteredCourses() {
      if (this.sortBy) {
        return _sortBy(this.courses);
      }
      return this.courses;
    },
    courses() {
      return this.$store.state.courses.listCourses;
    },
  },

  watch: {
    dialog(val) {
      // eslint-disable-next-line
      val || this.close();
    },
  },

  methods: {
    toggleAddCurriculumDialog() {
      this.addCurriculumDialog = !this.addCurriculumDialog;
      this.forceRerender();
    },
    toggleEditCurriculumDialog(item = null) {
      this.editedItem = item;
      this.editedItem.assignedDealer = _map(this.editedItem.dealers, 'id');
      this.editedItem.assignedSpecificDealers = _map(
        this.editedItem.specific_dealers,
        'id'
      );
      this.editCurriculumDialog = !this.editCurriculumDialog;
      this.forceRerender();
    },

    toggleClose() {
      this.addCurriculumDialog = false;
      this.editCurriculumDialog = false;
    },

    forceRerender() {
      this.key += 1;
    },

    closeConfirmDialog() {
      this.showConfirmDialog = false;
      this.selectedIdForDeletion = '';
      this.isDeleting = false;
    },

    previewConfirmDialog({ id }) {
      this.selectedIdForDeletion = id;
      this.showConfirmDialog = true;
    },

    toggleAllFilter() {
      this.filters = ['Active', 'Inactive', 'Published', 'Drafts'];
      this.fetchCurriculums();
    },

    removeAllFilter() {
      this.filters = [];
      this.fetchCurriculums();
    },

    sort(val) {
      this.sortBy = _head(val);
    },

    updateSorting() {
      this.pagination.current_page = 1;
      this.fetchCurriculums();
    },

    searchCurriculums() {
      (this.options.page = 1), (this.pagination.current_page = 1);
      this.fetchCurriculums();
    },

    fetchCurriculums() {
      this.isFetching = true;
      const filter =
        this.filters.length >= 4
          ? ['all']
          : this.filters.map(function (filter) {
              return filter.toLowerCase();
            });
      const search = this.search !== null ? this.search : '';
      this.axios
        .get('lms-manager/courses', {
          params: {
            page: this.pagination.current_page,
            per_page: this.options.itemsPerPage,
            type: filter,
            search: search,
            sortBy: this.options.sortBy[0],
            sortDesc: this.options.sortDesc[0],
          },
        })
        .then(({ data }) => {
          this.$store.commit('courses/setCourses', data.data);
          this.pagination.total = data.total;
          this.pagination.current_page = data.current_page;
          this.pagination.per_page = Number(data.per_page);
        })
        .catch(() => {
          this.$notify(
            'info',
            'Ooops! Please add filter(s) to the course manager table.'
          );
          this.$store.commit('courses/setCourses', []);
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => (this.isFetching = false));
    },

    changePage(page) {
      this.pagination.current_page = page;
      this.fetchCurriculums();
    },

    duplicateItem(item) {
      console.log(item);
      this.dupItem = item;
      this.showDuplicateItem = true;
    },

    editItem(item) {
      this.editedIndex = this.courses.indexOf(item);
      this.editedItem.name = item.name;
      const { dealers } = item;
      this.editedItem.assignedDealer = item.dealers;
      this.dialog = true;
    },

    deleteItem() {
      this.isDeleting = true;
      this.$store
        .dispatch('courses/deleteCourse', this.selectedIdForDeletion)
        .then(() => {
          this.$notify('success', 'Course removed!');
          this.fetchCurriculums();
          EventBus.$emit('RELOAD_TABLE');
          this.closeConfirmDialog();
        });
    },

    trimString(string) {
      let count = 20;

      if (string.length <= count) {
        return string;
      } else {
        return string.slice(0, count) + '...';
      }
    },

    trimArray(array) {
      let number = 1;
      let count = array.length - number;

      if (count <= 3) {
        return array
          .map(function (item) {
            return item.name;
          })
          .toString();
      } else {
        return (
          array
            .slice(0, number)
            .map(function (item) {
              return item.name;
            })
            .toString() +
          ' and ' +
          count +
          ' others'
        );
      }
    },

    restoreItem(item) {
      const { id } = item;
      this.$swal({
        title: 'Are you sure?',
        text: 'This action will put the course to active.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch('courses/restoreCourse', id).then(() => {
            this.$notify('success', 'Course restored');
            this.fetchCurriculums();
            EventBus.$emit('RELOAD_TABLE');
          });
        }
      });
    },

    close() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
      this.$validator.reset();
      this.dialog = false;
    },

    store() {
      const assignedDealers = _map(
        this.editedItem.assignedDealer,
        (assignedDealer) => {
          return assignedDealer.id;
        }
      );
      this.isSaving = true;
      this.$http
        .post('/lms-manager/courses', {
          name: this.editedItem.name,
          assigned_dealers: assignedDealers,
        })
        .then(({ data }) => {
          this.$store.dispatch('courses/addCourse', _head(data));
          this.$notify('success', 'Course added!');
          this.close();
        })
        .catch((errors) => {
          this.interactWithErrorBag(errors.response.data.errors, this.errors);
        })
        .finally(() => {
          this.isSaving = false;
          this.fetchCurriculums();
        });
    },

    update(index) {
      const { id } = this.courses[index];
      const payload = {
        id,
        name: this.editedItem.name,
        assigned_dealers: _map(
          this.editedItem.assignedDealer,
          (assignedDealer) => {
            return assignedDealer.id;
          }
        ),
      };
      this.isSaving = true;
      this.$http
        .patch(`/lms-manager/courses/${id}`, payload)
        .then(({ data }) => {
          const result = _head(data);
          this.$store.commit('courses/editCourse', {
            index,
            result,
          });
          this.$notify('success', 'Course updated!');
          this.close();
        })
        .catch((errors) => {
          this.interactWithErrorBag(errors, this.errors);
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => {
          this.isSaving = false;
          this.fetchCurriculums();
        });
    },

    save() {
      this.$validator.validate('*').then((result) => {
        if (result) {
          if (this.editedIndex > -1) {
            this.update(this.editedIndex);
          } else {
            this.store();
          }
        }
      });
    },
  },
};
</script>
<style lang="stylus">
.multiselect__tags {
  border-radius: 0px !important;
  border: 0px !important;
  border-bottom: 1px solid #777777 !important;
}
</style>
